import { useMemo, useState, useEffect } from 'react';
import { Divider, Box, Grid, Typography, Paper, Button, TextareaAutosize } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import SwitchInput from '../../../Components/SwitchInput';
import { fetchExperimentalBlockSites, fetchSelectiveRolloutSites } from '../../../api/expbDoc';
import { triggerSrSync } from '../../../api/sites';
import useStyles from './styles';

export default function SyncSelectedSitesPanel({
	selectedSites,
	setSelectedSites,
	loading,
	networkWideSyncHandler,
	setLoading
}) {
	const [siteInput, setSiteInput] = useState('');

	const [excludeSR, setExcludeSR] = useState(false);
	const [excludeBlocked, setExcludeBlocked] = useState(false);
	const [syncSRFromBranch, setSyncSRFromBranch] = useState(false);

	const [blockedSites, setBlockedSites] = useState([]);
	const [srSitesMap, setSrSitesMap] = useState({});
	const [loadingBlockedSites, setLoadingBlockedSites] = useState(false);
	const classes = useStyles();

	useEffect(() => {
		const fetchBlockedSites = async () => {
			try {
				setLoadingBlockedSites(true);

				const experimentSitesRes = await fetchExperimentalBlockSites();
				const selectiveRolloutSitesRes = await fetchSelectiveRolloutSites();

				const { experimentalSites = [], nonActiveSites = [] } =
					experimentSitesRes?.data?.data || {};

				const mergedSites = Array.from(
					new Set(
						[...experimentalSites, ...nonActiveSites].filter(
							(siteId) => siteId !== null && siteId !== ''
						)
					)
				);

				setBlockedSites(mergedSites.map((id) => String(id).trim()));
				setSrSitesMap(selectiveRolloutSitesRes?.data?.data?.selectiveRolloutSitesObj || {});
			} catch (err) {
				console.error('Failed to fetch blocked sites', err);
			} finally {
				setLoadingBlockedSites(false);
			}
		};

		fetchBlockedSites();
	}, []);

	/**
	 * @description Parses raw textarea input into an array of trimmed, non-empty site IDs.
	 * @returns {string[]} List of cleaned site IDs
	 */
	const normalizedSites = useMemo(() => {
		const rawSiteIds = siteInput.split('\n');

		const trimmedSiteIds = rawSiteIds.map((siteId) => siteId.trim()).filter(Boolean);

		return [...new Set(trimmedSiteIds)];
	}, [siteInput]);

	/**
	 * @description Filters site IDs that belong to SR (Selective Rollout) based on srSitesMap.
	 * @returns {string[]} List of SR-enabled site IDs
	 */
	const sitesForSRSync = useMemo(() => {
		if (!syncSRFromBranch) return [];

		return normalizedSites.filter((siteId) => srSitesMap[siteId]);
	}, [normalizedSites, srSitesMap, syncSRFromBranch]);

	/**
	 * @description Computes sites to exclude based on SR exclusion and blocked sites flags.
	 *              - Excludes SR sites if excludeSR is true.
	 *              - Excludes blocked sites if excludeBlocked is true.
	 * @returns {string[]} List of excluded site IDs
	 */
	const excludedSites = useMemo(() => {
		const result = [];
		const blockedSitesSet = new Set(blockedSites);

		if (excludeSR) {
			result.push(...normalizedSites.filter((id) => srSitesMap[id]));
		}

		if (excludeBlocked) {
			result.push(...normalizedSites.filter((id) => blockedSitesSet.has(id)));
		}

		return Array.from(new Set(result));
	}, [excludeSR, excludeBlocked, normalizedSites, blockedSites, srSitesMap]);

	/**
	 * @description Prepares payload for SR sync API containing siteId and corresponding consumer configs.
	 * @returns {Array<{siteId: string, consumerConfigs: any[]}>}
	 */
	const srSitesPayload = useMemo(
		() =>
			sitesForSRSync.map((siteId) => ({
				siteId,
				consumerConfigs: srSitesMap[siteId] || []
			})),
		[sitesForSRSync, srSitesMap]
	);

	/**
	 * @description Excludes sites present in excludedSites.
	 * @returns {string[]} List of production-ready site IDs
	 */
	const sitesForProductionSync = useMemo(() => {
		const excludedSitesSet = new Set(excludedSites);
		return normalizedSites.filter((siteId) => !excludedSitesSet.has(siteId));
	}, [normalizedSites, excludedSites]);

	useEffect(() => {
		setSelectedSites(sitesForProductionSync);
	}, [sitesForProductionSync, setSelectedSites]);

	const handleNormalSync = () => {
		if (!sitesForProductionSync.length) return;
		networkWideSyncHandler();
	};

	const handleSRSync = async () => {
		if (!sitesForSRSync.length) return;
		setLoading((prevState) => ({
			...prevState,
			triggeringSRSync: true
		}));
		try {
			await triggerSrSync({ srSitesObj: srSitesPayload });
			toast.success('Triggered Sync', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} catch (err) {
			const errorMessage = err?.response?.data?.data?.message || err.message;
			console.error('Failed to trigger SR sync', errorMessage);
			toast.error(errorMessage, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} finally {
			setLoading((prevState) => ({
				...prevState,
				triggeringSRSync: false
			}));
		}
	};

	const renderTextareaOrMessage = (data, emptyMessage) => {
		if (!data || data.length === 0) {
			return (
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					{emptyMessage}
				</Typography>
			);
		}

		return (
			<TextareaAutosize
				minRows={8}
				maxRows={8}
				style={{ width: '100%', padding: '1rem', resize: 'none' }}
				value={data.join('\n')}
				readOnly
			/>
		);
	};

	return (
		<Grid container spacing={2} sx={{ width: '100%' }}>
			<Grid item xs={12} md={4}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Typography variant="h6" mb={2}>
						Configure Sites
					</Typography>

					<Box className={classes.syncOptionsContainer}>
						<Typography
							variant="subtitle1"
							fontWeight={700}
							sx={{
								mb: 1.5
							}}
						>
							Sync Options
						</Typography>

						<SwitchInput
							defaultChecked={false}
							isObjectEditor={false}
							label="Sync SR Sites via SR"
							directValue={syncSRFromBranch}
							updateDirectValue={(value) => {
								setSyncSRFromBranch(value);
								if (value) {
									setExcludeSR(true);
								}
							}}
							containerStyles={{ mt: 2 }}
							disabled={loadingBlockedSites}
							infoMessage="Sync SR Sites via SR Branch"
							showInfoButton={true}
						/>

						<SwitchInput
							label="Exclude SR Sites"
							defaultChecked={false}
							isObjectEditor={false}
							directValue={excludeSR}
							updateDirectValue={setExcludeSR}
							disabled={syncSRFromBranch || loadingBlockedSites}
							infoMessage="Exclude SR Sites From Production Sync"
							showInfoButton={true}
						/>

						<SwitchInput
							label="Exclude Blocked Sites"
							defaultChecked={false}
							isObjectEditor={false}
							directValue={excludeBlocked}
							updateDirectValue={setExcludeBlocked}
							disabled={loadingBlockedSites}
							infoMessage="Exclude Blocked Sites From Production Sync"
							showInfoButton={true}
						/>
					</Box>

					<Box>
						<Typography variant="h7" fontWeight={600} sx={{ mb: 1 }}>
							Site IDs
						</Typography>

						<TextareaAutosize
							minRows={8}
							maxRows={8}
							placeholder="Add Site IDs to include (1 Site ID per line)..."
							className={classes.siteInputTextarea}
							value={siteInput}
							onChange={(e) => setSiteInput(e.target.value)}
							disabled={loading.triggeringSync}
						/>
					</Box>
					<Box
						sx={{
							mt: 3,
							display: 'flex',
							gap: 2
						}}
					>
						<LoadingButton
							fullWidth
							variant="contained"
							loading={loading.triggeringSync === true}
							onClick={handleNormalSync}
							disabled={!sitesForProductionSync.length || loading.triggeringSync === true}
						>
							Production Sync ({sitesForProductionSync.length})
						</LoadingButton>

						<LoadingButton
							fullWidth
							variant="contained"
							loading={loading.triggeringSRSync === true}
							onClick={handleSRSync}
							disabled={
								!syncSRFromBranch || !sitesForSRSync.length || loading.triggeringSRSync === true
							}
						>
							Sync SR ({sitesForSRSync.length})
						</LoadingButton>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12} md={8}>
				<Paper sx={{ p: 3 }}>
					<Typography variant="h6">Preview</Typography>

					<Typography mb={2}>
						Production: {sitesForProductionSync.length} | SR:{' '}
						{syncSRFromBranch ? sitesForSRSync.length : 0} | Excluded: {excludedSites.length}
					</Typography>

					<Divider sx={{ mb: 2 }} />

					<Grid container spacing={2}>
						{/* Production + Excluded (Grouped) */}
						<Grid item xs={12} md={6}>
							<Paper sx={{ p: 2, height: '100%' }}>
								<Typography fontWeight={600} mb={1}>
									Production Sites ({sitesForProductionSync.length})
								</Typography>

								{renderTextareaOrMessage(
									sitesForProductionSync,
									'No sites available for production sync'
								)}

								<Divider sx={{ my: 2 }} />

								<Typography fontWeight={600} color="error">
									Excluded Sites ({excludedSites.length})
								</Typography>

								{renderTextareaOrMessage(excludedSites, 'No excluded sites')}
							</Paper>
						</Grid>

						{/* SR Section */}
						<Grid item xs={12} md={6}>
							<Paper sx={{ p: 2, height: '100%' }}>
								<Typography fontWeight={600} mb={1}>
									SR Sites ({syncSRFromBranch ? sitesForSRSync.length : 0})
								</Typography>

								{!syncSRFromBranch ? (
									<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
										SR sync is disabled
									</Typography>
								) : (
									renderTextareaOrMessage(sitesForSRSync, 'No SR sites available')
								)}
							</Paper>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
		</Grid>
	);
}
